import React, { useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Select,
  FormControl,
  FormLabel,
  Text,
  Flex,
  Icon,
  VStack,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { HiOutlineCloudArrowUp, HiOutlineDocument, HiXMark } from "react-icons/hi2";
import { BASE_API_URL } from "../Components/config";
// Chakra modals render in a portal on <body>, outside the .stande-classroom
// wrapper, so we use the literal palette values rather than the CSS variables.
import { PALETTES } from "./classroomColors";

const SendAttachmentModal = ({ isOpen, onClose, courseGuid, students = [], isDark = true, onSent }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const [recipient, setRecipient] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const token = localStorage.getItem("accessToken");

  const studentName = (s) =>
    [s.first_name, s.last_name].filter(Boolean).join(" ").trim() || s.email;

  const reset = () => {
    setRecipient("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const handleSend = async () => {
    if (!recipient || !file) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("course_guid", courseGuid);
      form.append("recipient_user_guid", recipient);
      form.append("file", file);

      const res = await fetch(`${BASE_API_URL}/api/v1/student-attachments/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }, // browser sets multipart boundary
        body: form,
      });
      if (!res.ok) throw new Error("send failed");
      const created = await res.json();
      toast({ title: "File sent", status: "success", duration: 2500, isClosable: true });
      if (onSent) onSent(created);
      reset();
      onClose();
    } catch (e) {
      toast({
        title: "Could not send the file",
        description: "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay bg={p.overlay} backdropFilter="blur(2px)" />
      <ModalContent
        className="stande-classroom"
        data-theme={isDark ? "dark" : "light"}
        bg={p.surface}
        color={p.text}
        borderRadius="16px"
        border="1px solid"
        borderColor={p.border}
        boxShadow="0 24px 60px -20px rgba(0,0,0,0.5)"
        mx="16px"
      >
        <ModalHeader fontSize="18px" fontWeight="600" letterSpacing="-0.2px">
          Send a file to a student
        </ModalHeader>
        <ModalCloseButton borderRadius="full" color={p.textSub} _hover={{ bg: p.hover }} />

        <ModalBody>
          <VStack spacing="16px" align="stretch">
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="500" color={p.textSub}>
                Student
              </FormLabel>
              <Select
                placeholder={students.length ? "Select a student" : "No students in this class yet"}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                isDisabled={students.length === 0}
                height="42px"
                borderRadius="10px"
                bg={p.inputBg}
                color={p.text}
                borderColor={p.inputBorder}
                fontSize="14px"
                _hover={{ borderColor: p.textMuted }}
                _focus={{ borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` }}
              >
                {students.map((s) => (
                  <option key={s.user_guid} value={s.user_guid}>
                    {studentName(s)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="13px" fontWeight="500" color={p.textSub}>
                File
              </FormLabel>
              {!file ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py="22px"
                  borderRadius="12px"
                  border="1px dashed"
                  borderColor={p.inputBorder}
                  background={p.surfaceSubtle}
                  cursor="pointer"
                  transition="border-color 0.15s"
                  _hover={{ borderColor: p.accent }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon as={HiOutlineCloudArrowUp} boxSize="22px" color={p.accent} mb="6px" />
                  <Text fontSize="13px" fontWeight="500" color={p.text}>
                    Click to choose a file
                  </Text>
                  <Text fontSize="11px" color={p.textMuted}>
                    PDF, image, document — any file
                  </Text>
                </Flex>
              ) : (
                <Flex
                  align="center"
                  justify="space-between"
                  gap="10px"
                  px="12px"
                  py="10px"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor={p.border}
                  background={p.surfaceSubtle}
                >
                  <Flex align="center" gap="8px" minWidth="0">
                    <Icon as={HiOutlineDocument} boxSize="16px" color={p.textSub} />
                    <Text fontSize="13px" color={p.text} noOfLines={1}>
                      {file.name}
                    </Text>
                  </Flex>
                  <Icon
                    as={HiXMark}
                    boxSize="16px"
                    color={p.textMuted}
                    cursor="pointer"
                    _hover={{ color: p.text }}
                    onClick={() => {
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  />
                </Flex>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={handleClose} color={p.textSub} fontSize="14px" _hover={{ bg: p.hover }}>
            Cancel
          </Button>
          <Button
            variant="unstyled"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            px="20px"
            height="40px"
            borderRadius="full"
            bg={p.accent}
            color={p.onAccent}
            fontSize="14px"
            fontWeight="600"
            onClick={handleSend}
            isDisabled={!recipient || !file || sending}
            opacity={!recipient || !file || sending ? 0.6 : 1}
            _hover={{ bg: p.accentHover }}
          >
            {sending && <Spinner size="sm" />}
            {sending ? "Sending…" : "Send file"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SendAttachmentModal;
